import { OrderImportField } from "@redotech/redo-fulfillment-common/import-schema";
import {
  CreateFulfillmentOrder,
  CreateFulfillmentOrderSchema,
} from "@redotech/redo-fulfillment-operations-common/create-fulfillment-order";

/**
 * Maps OrderImportField → CSV column header name.
 * Same shape as custom import `fieldMappings`.
 */
export type FieldToColumnMap = Partial<Record<OrderImportField, string>>;

/**
 * Canonical mapping from OrderImportField → Zod schema path(s) in CreateFulfillmentOrderSchema.
 */
const FIELD_TO_SCHEMA_PATHS: Record<OrderImportField, string[]> = {
  [OrderImportField.ORDER_NUMBER]: ["orderInfo.orderNumber"],
  [OrderImportField.ORDER_DATE]: ["orderInfo.orderDate"],
  [OrderImportField.DATE_PAID]: ["orderInfo.paidDate"],
  [OrderImportField.FULFILL_BY]: ["fulfillBy"],
  [OrderImportField.BUYER_EMAIL]: ["address.email"],
  [OrderImportField.BUYER_PHONE]: ["address.phone"],
  [OrderImportField.RECIPIENT_FULL_NAME]: ["address.name"],
  [OrderImportField.RECIPIENT_FIRST_NAME]: ["address.name"],
  [OrderImportField.RECIPIENT_LAST_NAME]: ["address.name"],
  [OrderImportField.RECIPIENT_COMPANY]: ["address.company"],
  [OrderImportField.ADDRESS_LINE_1]: ["address.address1"],
  [OrderImportField.ADDRESS_LINE_2]: ["address.address2"],
  [OrderImportField.CITY]: ["address.city"],
  [OrderImportField.STATE]: ["address.province"],
  [OrderImportField.POSTAL_CODE]: ["address.zip"],
  [OrderImportField.COUNTRY_CODE]: ["address.countryCode"],
  [OrderImportField.ITEM_SKU]: ["orderInfo.items.*.sku"],
  [OrderImportField.ITEM_NAME]: ["orderInfo.items.*.title"],
  [OrderImportField.ITEM_QUANTITY]: ["orderInfo.items.*.totalQuantity"],
  [OrderImportField.ITEM_UNIT_PRICE]: [
    "orderInfo.items.*.unitPrice.amount",
    "orderInfo.items.*.totalPrice.amount",
    "orderInfo.items.*.unitDiscountedPrice.amount",
    "orderInfo.items.*.totalDiscountedPrice.amount",
  ],
  [OrderImportField.ITEM_WEIGHT_OZ]: ["orderInfo.items.*.weight.value"],
  [OrderImportField.ORDER_WEIGHT_LBS]: ["weight.value"],
  [OrderImportField.ORDER_DIMENSIONS]: [],
  [OrderImportField.INTERNAL_NOTES]: ["orderInfo.internalNote"],
  [OrderImportField.NOTES_TO_BUYER]: ["orderInfo.noteToBuyer"],
  [OrderImportField.GIFT_MESSAGE]: ["orderInfo.noteToBuyer"],
};

/** Reverse lookup: normalized zod path → OrderImportField */
function buildPathToFieldMap(): Map<string, OrderImportField> {
  const map = new Map<string, OrderImportField>();
  for (const [field, paths] of Object.entries(FIELD_TO_SCHEMA_PATHS)) {
    for (const path of paths) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      map.set(path, field as OrderImportField);
    }
  }
  return map;
}

const pathToFieldMap = buildPathToFieldMap();

/**
 * Converts a Zod issue path like ["orderInfo", "items", 0, "unitPrice", "amount"]
 * into a normalized key and resolves to the CSV column name via OrderImportField.
 */
function resolveColumnName(
  path: (string | number)[],
  columnMap: FieldToColumnMap,
): string | undefined {
  const normalized = path
    .map((segment) => (typeof segment === "number" ? "*" : segment))
    .join(".");

  const field = pathToFieldMap.get(normalized);
  if (field) {
    return columnMap[field];
  }

  // Try progressively shorter suffixes for nested fields
  const parts = normalized.split(".");
  for (let i = 1; i < parts.length; i++) {
    const suffix = parts.slice(i).join(".");
    const suffixField = pathToFieldMap.get(suffix);
    if (suffixField) {
      return columnMap[suffixField];
    }
  }
  return undefined;
}

interface ValidationError {
  orderNumber: string;
  messages: string[];
}

export function validateFulfillmentOrders(
  orders: unknown[],
  columnMap?: FieldToColumnMap,
): { valid: true } | { valid: false; errors: ValidationError[] } {
  const errors: ValidationError[] = [];

  for (const [i, order] of orders.entries()) {
    const result = CreateFulfillmentOrderSchema.safeParse(order);
    if (!result.success) {
      const orderNumber =
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        (order as { orderInfo?: { orderNumber?: string } })?.orderInfo
          ?.orderNumber ?? `Order ${i + 1}`;
      const messages = result.error.issues.map((issue) => {
        const path = issue.path.map((p) =>
          typeof p === "symbol" ? String(p) : p,
        );
        const columnName = columnMap
          ? resolveColumnName(path, columnMap)
          : undefined;
        if (columnName) {
          return `"${columnName}": ${issue.message}`;
        }
        const pathStr = path.join(" > ");
        return pathStr ? `${pathStr}: ${issue.message}` : issue.message;
      });
      errors.push({ orderNumber, messages });
    }
  }

  if (errors.length > 0) {
    return { valid: false, errors };
  }
  return { valid: true };
}

export interface ParseError {
  orderIdentifier: string;
  message: string;
}

export interface ParseResult {
  orders: CreateFulfillmentOrder[];
  parseErrors: ParseError[];
}

export async function collectParseResults<T>(
  items: T[],
  buildOrder: (item: T, index: number) => Promise<CreateFulfillmentOrder>,
  getIdentifier: (item: T, index: number) => string,
): Promise<ParseResult> {
  const result: ParseResult = { orders: [], parseErrors: [] };

  await Promise.all(
    items.map(async (item, i) => {
      try {
        result.orders.push(await buildOrder(item, i));
      } catch (error) {
        result.parseErrors.push({
          orderIdentifier: getIdentifier(item, i),
          message: error instanceof Error ? error.message : String(error),
        });
      }
    }),
  );

  return result;
}

export function formatParseErrors(errors: ParseError[]): string {
  return errors.map((e) => `${e.orderIdentifier}: ${e.message}`).join("\n");
}

export function formatValidationErrors(errors: ValidationError[]): string {
  if (errors.length === 1) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const error = errors[0]!;
    return `${error.orderNumber} has invalid data: ${error.messages.join("; ")}`;
  }
  return errors
    .map((e) => `${e.orderNumber}: ${e.messages.join("; ")}`)
    .join("\n");
}
